import React, { useState, useEffect, useContext, useRef } from 'react';
import { UserContext } from '../../contexts/UserContext';
import {
  Plus,
  Copy,
  Edit2,
  Trash2,
  Calendar,
  Users,
  FileText,
  Search,
  X,
  Eye,
  Download,
  Share2,
  CheckCircle,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  BookTemplate,
  Save,
  FileSpreadsheet,
  ChevronDown,
  File,
  Upload
} from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

const ToolboxTalks = () => {
  const { user } = useContext(UserContext);
  const activityInputRef = useRef(null);
  const [toolboxTalks, setToolboxTalks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [people, setPeople] = useState([]);
  const [availableActivities, setAvailableActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingTalk, setViewingTalk] = useState(null);
  const [editingTalk, setEditingTalk] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [participantSearchTerm, setParticipantSearchTerm] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [participantsManuallySet, setParticipantsManuallySet] = useState(false); // Track if participants were manually set (edit mode or user added)
  const [newWorkActivity, setNewWorkActivity] = useState('');
  const [showActivitySuggestions, setShowActivitySuggestions] = useState(false);
  const [selectedProject, setSelectedProject] = useState('all'); // 'all' or project name
  const [newCustomField, setNewCustomField] = useState({ label: '', value: '' });
  const [talksToSign, setTalksToSign] = useState([]);
  const [showSigningModal, setShowSigningModal] = useState(false);

  // New filter states
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all'); // 'all', 'shared', 'not_shared'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'date', 'topic', 'project'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  const [selectedSupervisor, setSelectedSupervisor] = useState('all');
  const [selectedParticipant, setSelectedParticipant] = useState('all');
  const [activeQuickFilter, setActiveQuickFilter] = useState(null);

  // Template states
  const [templates, setTemplates] = useState([]);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);

  // Export dropdown state
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const exportDropdownRef = useRef(null);

  // PDF-only mode for quick upload
  const [isPdfOnlyMode, setIsPdfOnlyMode] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    time: '',
    project: '',
    topic: '',
    conductor: user?.firstName + ' ' + user?.lastName || '',
    signature: '',
    participants: '',
    agenda_items: '',
    work_activities: [],
    hazards: '',
    preventive_measures: '',
    notes: '',
    hse_comments: '',
    custom_fields: [],
    pdf_document: null,
    // Subjects Discussed (matching backend field names)
    type_of_operation: false,
    crane_requirements: false,
    methods_procedures: false,
    potential_hazards: false,
    permit_to_work: false,
    equipment_to_be_used: false,
    safety_spill_equipment: false,
    materials_chemicals: false,
    responsibilities: false,
    isolations_necessary: false,
    access_egress: false,
    impact_adjacent_work: false,
    tests_monitoring: false,
    environmental_hazards: false,
    work_environment: false,
    waste_management: false,
    general_other_info: false,
    flora_fauna_archeological: false,
    custom_subjects: []
  });

  const [newCustomSubject, setNewCustomSubject] = useState('');

  useEffect(() => {
    fetchToolboxTalks();
    fetchProjects();
    fetchPeople();
    fetchTalksToSign();
    fetchTemplates();
  }, []);

  const fetchTalksToSign = async () => {
    try {
      console.log('Fetching toolbox talks to sign...');
      console.log('User ID:', user?.id);

      const response = await axios.get(`${API_BASE_URL}/toolbox-talks/to-sign/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });

      console.log('Response from /to-sign/:', response.data);

      // Filter only unsigned talks
      const unsigned = response.data.filter(talk => !talk.has_signed);
      console.log('Unsigned talks:', unsigned);
      console.log('Number of unsigned talks:', unsigned.length);

      setTalksToSign(unsigned);

      // Auto-show modal if there are unsigned talks
      if (unsigned.length > 0) {
        console.log('Showing signing modal...');
        setShowSigningModal(true);
      } else {
        console.log('No unsigned talks to show');
      }
    } catch (error) {
      console.error('Error fetching talks to sign:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  // Close activity suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activityInputRef.current && !activityInputRef.current.contains(event.target)) {
        setShowActivitySuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-select participants based on project assignments for the selected date
  // Only auto-fetch if participants were not manually set (e.g., in edit mode or user added manually)
  useEffect(() => {
    const fetchProjectParticipants = async () => {
      // Skip if participants were manually set (edit mode or user interaction)
      if (participantsManuallySet) {
        console.log('Skipping auto-fetch - participants were manually set');
        return;
      }

      if (!formData.project || !formData.date) {
        console.log('Skipping participant fetch - missing project or date:', { project: formData.project, date: formData.date });
        return;
      }

      try {
        // Find the selected project ID
        const selectedProject = projects.find(p => p.name === formData.project);
        console.log('Selected project:', selectedProject);
        if (!selectedProject) {
          console.log('Project not found in projects list');
          return;
        }

        // Fetch resource planning assignments for this project and date
        console.log('Fetching assignments for:', { projectId: selectedProject.id, date: formData.date });
        const response = await axios.get(`${API_BASE_URL}/resource-planning-assignments/`, {
          params: {
            project: selectedProject.id,
            start_date: formData.date,
            end_date: formData.date
          },
          headers: { Authorization: `Bearer ${user.accessToken}` }
        });

        console.log('Resource planning assignments response:', response.data);

        // Extract unique user IDs from all assignments
        // ResourcePlanningAssignment has a 'user' field (single user per assignment)
        const userIds = new Set();
        response.data.forEach(assignment => {
          console.log('Assignment:', assignment);
          // Check for user or user_id field
          if (assignment.user) {
            userIds.add(assignment.user);
          } else if (assignment.user_id) {
            userIds.add(assignment.user_id);
          }
        });

        console.log('Extracted user IDs:', Array.from(userIds));

        // Auto-select these participants
        setSelectedParticipants(Array.from(userIds));
      } catch (error) {
        console.error('Error fetching project participants:', error);
      }
    };

    fetchProjectParticipants();
  }, [formData.project, formData.date, projects, participantsManuallySet]);

  // Extract unique topics from toolbox talks
  const uniqueTopics = React.useMemo(() => {
    const topics = new Set();
    toolboxTalks.forEach(talk => {
      if (talk.topic) topics.add(talk.topic);
    });
    return Array.from(topics).sort();
  }, [toolboxTalks]);

  // Extract unique supervisors
  const uniqueSupervisors = React.useMemo(() => {
    const supervisors = new Set();
    toolboxTalks.forEach(talk => {
      if (talk.conductor) supervisors.add(talk.conductor);
    });
    return Array.from(supervisors).sort();
  }, [toolboxTalks]);

  // Extract unique participants
  const uniqueParticipants = React.useMemo(() => {
    const participantIds = new Set();
    toolboxTalks.forEach(talk => {
      if (talk.participants) {
        talk.participants.split(',').forEach(id => {
          const trimmedId = id.trim();
          if (trimmedId) participantIds.add(parseInt(trimmedId));
        });
      }
    });
    return Array.from(participantIds).map(id => {
      const person = people.find(p => p.id === id);
      return person ? { id, name: `${person.first_name} ${person.last_name}` } : null;
    }).filter(Boolean).sort((a, b) => a.name.localeCompare(b.name));
  }, [toolboxTalks, people]);

  const fetchToolboxTalks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/toolbox-talks/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      setToolboxTalks(response.data);

      console.log('Fetched toolbox talks:', response.data);

      // Extract unique activities from all toolbox talks
      const allActivities = new Set();
      response.data.forEach(talk => {
        console.log('Talk work_activities:', talk.work_activities);

        // Handle both array and string (in case backend returns JSON string)
        let activities = talk.work_activities;
        if (typeof activities === 'string') {
          try {
            activities = JSON.parse(activities);
          } catch (e) {
            console.log('Failed to parse work_activities as JSON');
          }
        }

        if (activities && Array.isArray(activities)) {
          activities.forEach(activity => {
            // Support both old string format and new object format
            const activityName = typeof activity === 'string' ? activity : activity.name;
            if (activityName) {
              allActivities.add(activityName);
            }
          });
        }
      });

      console.log('Available activities:', Array.from(allActivities));
      setAvailableActivities(Array.from(allActivities).sort());
    } catch (error) {
      console.error('Error fetching toolbox talks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/projects/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      // Filter only active projects (is_active !== false)
      const activeProjects = response.data.filter(project => project.is_active !== false);
      setProjects(activeProjects);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchPeople = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      setPeople(response.data);
    } catch (error) {
      console.error('Error fetching people:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/toolbox-talk-templates/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) {
      alert('Please enter a template name');
      return;
    }

    if (!formData.topic || !formData.topic.trim()) {
      alert('Please enter a topic before saving as template');
      return;
    }

    try {
      const templateData = {
        name: templateName,
        topic: formData.topic || '',
        work_activities: formData.work_activities || [],
        hazards: formData.hazards || '',
        preventive_measures: formData.preventive_measures || '',
        notes: formData.notes || '',
        hse_comments: formData.hse_comments || '',
        custom_fields: formData.custom_fields || [],
        type_of_operation: formData.type_of_operation || false,
        crane_requirements: formData.crane_requirements || false,
        methods_procedures: formData.methods_procedures || false,
        potential_hazards: formData.potential_hazards || false,
        permit_to_work: formData.permit_to_work || false,
        equipment_to_be_used: formData.equipment_to_be_used || false,
        safety_spill_equipment: formData.safety_spill_equipment || false,
        materials_chemicals: formData.materials_chemicals || false,
        responsibilities: formData.responsibilities || false,
        isolations_necessary: formData.isolations_necessary || false,
        access_egress: formData.access_egress || false,
        impact_adjacent_work: formData.impact_adjacent_work || false,
        tests_monitoring: formData.tests_monitoring || false,
        environmental_hazards: formData.environmental_hazards || false,
        work_environment: formData.work_environment || false,
        waste_management: formData.waste_management || false,
        general_other_info: formData.general_other_info || false,
        flora_fauna_archeological: formData.flora_fauna_archeological || false,
        custom_subjects: formData.custom_subjects || []
      };

      console.log('Saving template with data:', templateData);

      const response = await axios.post(`${API_BASE_URL}/toolbox-talk-templates/`, templateData, {
        headers: {
          Authorization: `Bearer ${user.accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Template saved successfully:', response.data);
      alert('Template saved successfully!');
      setShowSaveTemplateModal(false);
      setTemplateName('');
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);

      // Show more detailed error message
      const errorMsg = error.response?.data?.detail
        || error.response?.data?.message
        || JSON.stringify(error.response?.data)
        || 'Failed to save template';
      alert('Error saving template: ' + errorMsg);
    }
  };

  const handleCreateFromTemplate = (template) => {
    setEditingTalk(null);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      time: '',
      project: '',
      topic: template.topic || '',
      conductor: user?.firstName + ' ' + user?.lastName || '',
      signature: '',
      participants: '',
      agenda_items: '',
      work_activities: template.work_activities || [],
      hazards: template.hazards || '',
      preventive_measures: template.preventive_measures || '',
      notes: template.notes || '',
      hse_comments: template.hse_comments || '',
      custom_fields: template.custom_fields || [],
      pdf_document: null,
      type_of_operation: template.type_of_operation || false,
      crane_requirements: template.crane_requirements || false,
      methods_procedures: template.methods_procedures || false,
      potential_hazards: template.potential_hazards || false,
      permit_to_work: template.permit_to_work || false,
      equipment_to_be_used: template.equipment_to_be_used || false,
      safety_spill_equipment: template.safety_spill_equipment || false,
      materials_chemicals: template.materials_chemicals || false,
      responsibilities: template.responsibilities || false,
      isolations_necessary: template.isolations_necessary || false,
      access_egress: template.access_egress || false,
      impact_adjacent_work: template.impact_adjacent_work || false,
      tests_monitoring: template.tests_monitoring || false,
      environmental_hazards: template.environmental_hazards || false,
      work_environment: template.work_environment || false,
      waste_management: template.waste_management || false,
      general_other_info: template.general_other_info || false,
      flora_fauna_archeological: template.flora_fauna_archeological || false,
      custom_subjects: template.custom_subjects || []
    });
    setSelectedParticipants([]);
    setParticipantSearchTerm('');
    setNewCustomSubject('');
    setNewWorkActivity('');
    setNewCustomField({ label: '', value: '' });
    setShowTemplateLibrary(false);
    setShowModal(true);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/toolbox-talk-templates/${templateId}/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template');
    }
  };

  const handleQuickFilter = (filterType) => {
    const today = new Date();
    const oneWeekAgo = new Date(today);
    oneWeekAgo.setDate(today.getDate() - 7);
    const oneMonthAgo = new Date(today);
    oneMonthAgo.setMonth(today.getMonth() - 1);

    // Reset all filters first
    setSelectedTopic('all');
    setSelectedStatus('all');
    setSelectedSupervisor('all');
    setSelectedParticipant('all');
    setSearchTerm('');

    switch(filterType) {
      case 'today':
        setDateFrom(today.toISOString().split('T')[0]);
        setDateTo(today.toISOString().split('T')[0]);
        setActiveQuickFilter('today');
        break;
      case 'week':
        setDateFrom(oneWeekAgo.toISOString().split('T')[0]);
        setDateTo(today.toISOString().split('T')[0]);
        setActiveQuickFilter('week');
        break;
      case 'month':
        setDateFrom(oneMonthAgo.toISOString().split('T')[0]);
        setDateTo(today.toISOString().split('T')[0]);
        setActiveQuickFilter('month');
        break;
      case 'unsigned':
        setDateFrom('');
        setDateTo('');
        setSelectedStatus('not_shared');
        setActiveQuickFilter('unsigned');
        break;
      case 'my-talks':
        setDateFrom('');
        setDateTo('');
        // Filter will be applied in filteredTalks based on created_by
        setActiveQuickFilter('my-talks');
        break;
      case 'shared':
        setDateFrom('');
        setDateTo('');
        setSelectedStatus('shared');
        setActiveQuickFilter('shared');
        break;
      default:
        setDateFrom('');
        setDateTo('');
        setActiveQuickFilter(null);
    }
  };

  const prepareExportData = () => {
    const data = [];

    filteredTalks.forEach(talk => {
      const createdByUser = people.find(p => p.id === talk.created_by);
      const participantCount = talk.participants ? talk.participants.split(',').filter(id => id.trim()).length : 0;
      const signedCount = Object.keys(talk.participant_signatures || {}).length;
      const status = talk.is_shared ? `Shared (${signedCount}/${participantCount})` : 'Not shared';
      const workActivities = Array.isArray(talk.work_activities)
        ? talk.work_activities.map(activity => {
            const name = typeof activity === 'string' ? activity : activity.name;
            const turbine = typeof activity === 'string' ? '' : (activity.turbine || '');
            return turbine ? `${name} (${turbine})` : name;
          }).join('; ')
        : '';

      data.push({
        'Date': new Date(talk.date).toLocaleDateString(),
        'Project': talk.project || '',
        'Topic': talk.topic || '',
        'Supervisor/Lead': talk.conductor || '',
        'Created By': createdByUser ? `${createdByUser.first_name} ${createdByUser.last_name}` : '',
        'Participants Count': participantCount,
        'Signed Count': signedCount,
        'Status': status,
        'Work Activities': workActivities,
        'Notes': talk.notes || '',
        'HSE Comments': talk.hse_comments || ''
      });
    });

    return data;
  };

  const handleExportToCSV = () => {
    try {
      const data = prepareExportData();
      const headers = Object.keys(data[0] || {});
      const csvRows = [headers.join(',')];

      data.forEach(row => {
        const values = headers.map(header => {
          const value = row[header];
          // Escape quotes and wrap in quotes
          return `"${String(value).replace(/"/g, '""')}"`;
        });
        csvRows.push(values.join(','));
      });

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const today = new Date().toISOString().split('T')[0];
      const filename = `Toolbox_Talks_${today}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log(`Exported ${filteredTalks.length} toolbox talks to ${filename}`);
      setShowExportDropdown(false);
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      alert('Failed to export data');
    }
  };

  const handleExportToExcel = () => {
    try {
      const data = prepareExportData();

      // Create HTML table for Excel
      const headers = Object.keys(data[0] || {});
      let htmlTable = '<table border="1"><thead><tr>';

      headers.forEach(header => {
        htmlTable += `<th>${header}</th>`;
      });
      htmlTable += '</tr></thead><tbody>';

      data.forEach(row => {
        htmlTable += '<tr>';
        headers.forEach(header => {
          htmlTable += `<td>${row[header]}</td>`;
        });
        htmlTable += '</tr>';
      });
      htmlTable += '</tbody></table>';

      // Create Excel file
      const blob = new Blob([htmlTable], { type: 'application/vnd.ms-excel' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const today = new Date().toISOString().split('T')[0];
      const filename = `Toolbox_Talks_${today}.xls`;

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log(`Exported ${filteredTalks.length} toolbox talks to ${filename}`);
      setShowExportDropdown(false);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export data');
    }
  };

  const handleCreate = () => {
    setEditingTalk(null);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      time: '',
      project: '',
      topic: '',
      conductor: user?.firstName + ' ' + user?.lastName || '',
      signature: '',
      participants: '',
      agenda_items: '',
      work_activities: [],
      hazards: '',
      preventive_measures: '',
      notes: '',
      hse_comments: '',
      custom_fields: [],
      pdf_document: null,
      type_of_operation: false,
      crane_requirements: false,
      methods_procedures: false,
      potential_hazards: false,
      permit_to_work: false,
      equipment_to_be_used: false,
      safety_spill_equipment: false,
      materials_chemicals: false,
      responsibilities: false,
      isolations_necessary: false,
      access_egress: false,
      impact_adjacent_work: false,
      tests_monitoring: false,
      environmental_hazards: false,
      work_environment: false,
      waste_management: false,
      general_other_info: false,
      flora_fauna_archeological: false,
      custom_subjects: []
    });
    setSelectedParticipants([]);
    setParticipantsManuallySet(false); // Reset flag for new toolbox talk - allow auto-fetch
    setParticipantSearchTerm('');
    setNewCustomSubject('');
    setNewWorkActivity('');
    setNewCustomField({ label: '', value: '' });
    setIsPdfOnlyMode(false);
    setShowModal(true);
  };

  const handleToggleParticipant = (personId) => {
    // Mark as manually set when user adds/removes participants
    setParticipantsManuallySet(true);
    setSelectedParticipants(prev => {
      if (prev.includes(personId)) {
        return prev.filter(id => id !== personId);
      } else {
        return [...prev, personId];
      }
    });
  };

  const filteredPeople = people.filter(person => {
    const searchLower = participantSearchTerm.toLowerCase();
    const fullName = `${person.first_name || ''} ${person.last_name || ''}`.toLowerCase();
    const email = (person.email || '').toLowerCase();
    return fullName.includes(searchLower) || email.includes(searchLower);
  });

  const handleAddWorkActivity = (activity = null) => {
    const activityToAdd = activity || newWorkActivity.trim();
    if (!activityToAdd) return;

    // Get activity names from current list (support both old string format and new object format)
    const existingNames = formData.work_activities.map(a =>
      typeof a === 'string' ? a : a.name
    );

    // Don't add duplicates
    if (existingNames.includes(activityToAdd)) {
      setNewWorkActivity('');
      setShowActivitySuggestions(false);
      return;
    }

    // Add as object with name and turbine
    setFormData({
      ...formData,
      work_activities: [...formData.work_activities, { name: activityToAdd, turbine: '' }]
    });

    // Add to available activities if it's new
    if (!availableActivities.includes(activityToAdd)) {
      setAvailableActivities([...availableActivities, activityToAdd].sort());
    }

    setNewWorkActivity('');
    setShowActivitySuggestions(false);
  };

  const handleRemoveWorkActivity = (index) => {
    setFormData({
      ...formData,
      work_activities: formData.work_activities.filter((_, i) => i !== index)
    });
  };

  const handleEditWorkActivity = (index, field, value) => {
    const updated = [...formData.work_activities];
    // Handle both old string format and new object format
    if (typeof updated[index] === 'string') {
      updated[index] = { name: updated[index], turbine: '' };
    }
    updated[index][field] = value;
    setFormData({ ...formData, work_activities: updated });
  };

  // Filter activities that aren't already added and match search
  const getFilteredSuggestions = () => {
    // Get activity names from current list (support both formats)
    const existingNames = formData.work_activities.map(a =>
      typeof a === 'string' ? a : a.name
    );

    if (!newWorkActivity.trim()) return availableActivities.filter(a => !existingNames.includes(a));

    return availableActivities.filter(activity =>
      !existingNames.includes(activity) &&
      activity.toLowerCase().includes(newWorkActivity.toLowerCase())
    );
  };

  const handleAddCustomField = () => {
    if (!newCustomField.label.trim()) return;

    setFormData({
      ...formData,
      custom_fields: [...formData.custom_fields, { label: newCustomField.label, value: newCustomField.value }]
    });
    setNewCustomField({ label: '', value: '' });
  };

  const handleRemoveCustomField = (index) => {
    setFormData({
      ...formData,
      custom_fields: formData.custom_fields.filter((_, i) => i !== index)
    });
  };

  const handleUpdateCustomField = (index, field, value) => {
    const updated = [...formData.custom_fields];
    updated[index][field] = value;
    setFormData({ ...formData, custom_fields: updated });
  };

  const handleAddCustomSubject = () => {
    if (!newCustomSubject.trim()) return;

    setFormData({
      ...formData,
      custom_subjects: [...formData.custom_subjects, { text: newCustomSubject, checked: false }]
    });
    setNewCustomSubject('');
  };

  const handleRemoveCustomSubject = (index) => {
    setFormData({
      ...formData,
      custom_subjects: formData.custom_subjects.filter((_, i) => i !== index)
    });
  };

  const handleToggleCustomSubject = (index) => {
    const updated = [...formData.custom_subjects];
    updated[index].checked = !updated[index].checked;
    setFormData({ ...formData, custom_subjects: updated });
  };

  const handleEdit = (talk) => {
    setEditingTalk(talk);
    setFormData(talk);

    // Parse participants string to array of IDs (if stored as comma-separated IDs)
    if (talk.participants) {
      const participantIds = talk.participants.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      setSelectedParticipants(participantIds);
    } else {
      setSelectedParticipants([]);
    }

    // Mark participants as manually set to prevent auto-fetch from overwriting
    setParticipantsManuallySet(true);

    setShowModal(true);
  };

  const handleView = (talk) => {
    setViewingTalk(talk);
    setShowViewModal(true);
  };

  const handleCopy = (talk) => {
    setEditingTalk(null);
    setFormData({
      ...talk,
      id: undefined,
      date: new Date().toISOString().split('T')[0],
      conductor: user?.firstName + ' ' + user?.lastName || '',
    });

    // Parse participants for copying
    if (talk.participants) {
      const participantIds = talk.participants.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      setSelectedParticipants(participantIds);
      // Mark as manually set since we're copying existing participants
      setParticipantsManuallySet(true);
    } else {
      setSelectedParticipants([]);
      setParticipantsManuallySet(false);
    }

    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this toolbox talk?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/toolbox-talks/${id}/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      fetchToolboxTalks();
    } catch (error) {
      console.error('Error deleting toolbox talk:', error);
      alert('Failed to delete toolbox talk');
    }
  };

  const handleDownloadPDF = async (talk) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/toolbox-talks/${talk.id}/pdf/`,
        {
          headers: { Authorization: `Bearer ${user.accessToken}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Toolbox_Talk_${talk.project}_${talk.date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF');
    }
  };

  const handlePreviewPDF = async (talk) => {
    try {
      // Open PDF in new tab with preview=true parameter
      const url = `${API_BASE_URL}/toolbox-talks/${talk.id}/pdf/?preview=true`;

      // Create a temporary form to pass the authorization token
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${user.accessToken}` },
        responseType: 'blob'
      });

      // Create blob URL and open in new tab
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(blobUrl, '_blank');

      // Clean up after a delay
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Error previewing PDF:', error);
      alert('Failed to preview PDF');
    }
  };

  const handleShare = async (talk) => {
    if (!window.confirm('Are you sure you want to share this Toolbox Talk with participants for signing?')) return;

    try {
      await axios.post(
        `${API_BASE_URL}/toolbox-talks/${talk.id}/share/`,
        {},
        {
          headers: { Authorization: `Bearer ${user.accessToken}` }
        }
      );
      alert('Toolbox Talk shared successfully! Participants can now sign it.');
      fetchToolboxTalks();
    } catch (error) {
      console.error('Error sharing toolbox talk:', error);
      alert('Failed to share toolbox talk');
    }
  };

  const handleSignTalk = async (talkId) => {
    try {
      // Use user's predefined signature
      const signature = user.user_signature || '';

      await axios.post(
        `${API_BASE_URL}/toolbox-talks/${talkId}/sign/`,
        { signature },
        {
          headers: { Authorization: `Bearer ${user.accessToken}` }
        }
      );

      // Remove from unsigned list
      setTalksToSign(prev => prev.filter(t => t.id !== talkId));

      // Close modal if no more talks to sign
      if (talksToSign.length === 1) {
        setShowSigningModal(false);
      }

      alert('Toolbox Talk signed successfully!');
      fetchToolboxTalks();
    } catch (error) {
      console.error('Error signing toolbox talk:', error);
      alert(error.response?.data?.error || 'Failed to sign toolbox talk');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Convert selected participants to comma-separated IDs
      const participantsString = selectedParticipants.join(',');

      // Check if we have a PDF file to upload
      const hasPdfFile = formData.pdf_document && formData.pdf_document.name && formData.pdf_document.size;

      let dataToSend;
      let headers = {
        Authorization: `Bearer ${user.accessToken}`
      };

      if (hasPdfFile) {
        // Use FormData for file upload
        dataToSend = new FormData();

        // Add all form fields
        Object.keys(formData).forEach(key => {
          if (key === 'pdf_document') {
            if (formData[key] && formData[key].name) {
              dataToSend.append('pdf_document', formData[key]);
            }
          } else if (key === 'work_activities' || key === 'custom_fields' || key === 'custom_subjects') {
            dataToSend.append(key, JSON.stringify(formData[key]));
          } else if (formData[key] !== null && formData[key] !== '') {
            dataToSend.append(key, formData[key]);
          }
        });

        dataToSend.append('participants', participantsString);
        headers['Content-Type'] = 'multipart/form-data';
      } else {
        // Use JSON for regular submissions
        dataToSend = {
          ...formData,
          participants: participantsString,
          work_activities: formData.work_activities
        };
        // Remove pdf_document if it's null or not a file
        delete dataToSend.pdf_document;
        headers['Content-Type'] = 'application/json';
      }

      console.log('Data being sent to backend:', hasPdfFile ? 'FormData with PDF' : dataToSend);

      if (editingTalk) {
        await axios.put(`${API_BASE_URL}/toolbox-talks/${editingTalk.id}/`, dataToSend, { headers });
      } else {
        await axios.post(`${API_BASE_URL}/toolbox-talks/`, dataToSend, { headers });
      }

      setShowModal(false);
      setParticipantsManuallySet(false); // Reset after successful save
      await fetchToolboxTalks();
    } catch (error) {
      console.error('Error saving toolbox talk:', error);
      console.error('Error details:', error.response?.data);
      alert('Failed to save toolbox talk');
    }
  };

  const filteredTalks = React.useMemo(() => {
    let filtered = toolboxTalks.filter(talk => {
      // Filter by project
      const matchesProject = selectedProject === 'all' || talk.project === selectedProject;

      // Filter by search term
      const matchesSearch = !searchTerm ||
        talk.topic?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        talk.project?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        talk.conductor?.toLowerCase().includes(searchTerm.toLowerCase());

      // Filter by topic
      const matchesTopic = selectedTopic === 'all' || talk.topic === selectedTopic;

      // Filter by status (shared/not shared)
      const matchesStatus = selectedStatus === 'all' ||
        (selectedStatus === 'shared' && talk.is_shared) ||
        (selectedStatus === 'not_shared' && !talk.is_shared);

      // Filter by date range
      const matchesDateFrom = !dateFrom || new Date(talk.date) >= new Date(dateFrom);
      const matchesDateTo = !dateTo || new Date(talk.date) <= new Date(dateTo);

      // Filter by supervisor
      const matchesSupervisor = selectedSupervisor === 'all' || talk.conductor === selectedSupervisor;

      // Filter by participant
      const matchesParticipant = selectedParticipant === 'all' ||
        (talk.participants && talk.participants.split(',').map(id => parseInt(id.trim())).includes(selectedParticipant));

      // Filter by "My Talks"
      const matchesMyTalks = activeQuickFilter !== 'my-talks' || talk.created_by === user?.id;

      return matchesProject && matchesSearch && matchesTopic && matchesStatus &&
             matchesDateFrom && matchesDateTo && matchesSupervisor && matchesParticipant && matchesMyTalks;
    });

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;

      if (sortBy === 'date') {
        comparison = new Date(a.date) - new Date(b.date);
      } else if (sortBy === 'topic') {
        comparison = (a.topic || '').localeCompare(b.topic || '');
      } else if (sortBy === 'project') {
        comparison = (a.project || '').localeCompare(b.project || '');
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [toolboxTalks, selectedProject, searchTerm, selectedTopic, selectedStatus, dateFrom, dateTo, sortBy, sortOrder, selectedSupervisor, selectedParticipant, activeQuickFilter, user]);

  // Calculate statistics
  const statistics = React.useMemo(() => {
    const today = new Date();
    const oneWeekAgo = new Date(today);
    oneWeekAgo.setDate(today.getDate() - 7);

    const thisWeekTalks = toolboxTalks.filter(talk =>
      new Date(talk.date) >= oneWeekAgo && new Date(talk.date) <= today
    );

    const sharedTalks = toolboxTalks.filter(talk => talk.is_shared);
    const completionRate = toolboxTalks.length > 0
      ? Math.round((sharedTalks.length / toolboxTalks.length) * 100)
      : 0;

    // Find most active project
    const projectCounts = {};
    toolboxTalks.forEach(talk => {
      if (talk.project) {
        projectCounts[talk.project] = (projectCounts[talk.project] || 0) + 1;
      }
    });
    const mostActiveProject = Object.keys(projectCounts).length > 0
      ? Object.keys(projectCounts).reduce((a, b) => projectCounts[a] > projectCounts[b] ? a : b)
      : 'N/A';

    return {
      total: toolboxTalks.length,
      thisWeek: thisWeekTalks.length,
      completionRate,
      mostActiveProject
    };
  }, [toolboxTalks]);

  return (
    <div className="min-h-screen bg-steel-50 p-6">
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-slideInRight {
          animation: slideInRight 0.3s ease-out forwards;
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
      <div className="w-full">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-black text-slate-900 mb-2 uppercase tracking-wide">Toolbox Talks</h1>
          <p className="text-slate-600">Daily safety briefings and team communications</p>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-steel-200 p-4 mb-6">
          <div className="space-y-4">
            {/* First Row - Search and Create */}
            <div className="flex flex-wrap items-center gap-4">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search talks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-1.5 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                />
              </div>

              <div className="flex items-center gap-2 ml-auto">
                {/* Export Dropdown */}
                <div className="relative" ref={exportDropdownRef}>
                  <button
                    onClick={() => setShowExportDropdown(!showExportDropdown)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-brand-500 hover:bg-brand-600 text-white text-sm rounded-xl transition-all duration-200 font-medium shadow-sm hover:shadow-md"
                    title={`Export ${filteredTalks.length} filtered talks`}
                  >
                    <Download className="w-3.5 h-3.5" />
                    Export ({filteredTalks.length})
                    <ChevronDown className="w-3 h-3" />
                  </button>

                  {/* Dropdown Menu */}
                  {showExportDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-steel-200 z-50">
                      <div className="py-1">
                        <button
                          onClick={handleExportToCSV}
                          className="flex items-center gap-2 w-full px-4 py-2 text-sm text-slate-700 hover:bg-brand-50 hover:text-brand-700 transition-colors"
                        >
                          <File className="w-4 h-4" />
                          <div className="flex flex-col items-start">
                            <span className="font-medium">Export as CSV</span>
                            <span className="text-xs text-slate-500">Universal format</span>
                          </div>
                        </button>
                        <button
                          onClick={handleExportToExcel}
                          className="flex items-center gap-2 w-full px-4 py-2 text-sm text-slate-700 hover:bg-brand-50 hover:text-brand-700 transition-colors"
                        >
                          <FileSpreadsheet className="w-4 h-4" />
                          <div className="flex flex-col items-start">
                            <span className="font-medium">Export as Excel</span>
                            <span className="text-xs text-slate-500">Microsoft Excel format</span>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setShowTemplateLibrary(true)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm rounded-xl transition-all duration-200 font-medium shadow-lg shadow-brand-500/20 hover:shadow-md"
                >
                  <BookTemplate className="w-3.5 h-3.5" />
                  Templates ({templates.length})
                </button>
                <button
                  onClick={handleCreate}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-brand-500 hover:bg-brand-600 text-white text-sm rounded-xl transition-all duration-200 font-medium shadow-lg shadow-brand-500/20 hover:shadow-md"
                >
                  <Plus className="w-3.5 h-3.5" />
                  New Toolbox Talk
                </button>
              </div>
            </div>

            {/* Second Row - Filters */}
            <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-steel-100">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Filter className="w-4 h-4" />
                Filters:
              </div>

              {/* Project Filter */}
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="px-3 py-1.5 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all min-w-[180px]"
              >
                <option value="all">All Projects</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.name}>
                    {project.name}
                  </option>
                ))}
              </select>

              {/* Topic Filter */}
              <select
                value={selectedTopic}
                onChange={(e) => setSelectedTopic(e.target.value)}
                className="px-3 py-1.5 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
              >
                <option value="all">All Topics</option>
                {uniqueTopics.map((topic, index) => (
                  <option key={index} value={topic}>
                    {topic}
                  </option>
                ))}
              </select>

              {/* Status Filter */}
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-3 py-1.5 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
              >
                <option value="all">All Status</option>
                <option value="shared">Shared</option>
                <option value="not_shared">Not Shared</option>
              </select>

              {/* Supervisor Filter */}
              <select
                value={selectedSupervisor}
                onChange={(e) => setSelectedSupervisor(e.target.value)}
                className="px-3 py-1.5 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all min-w-[180px]"
              >
                <option value="all">All Supervisors</option>
                {uniqueSupervisors.map((supervisor, index) => (
                  <option key={index} value={supervisor}>
                    {supervisor}
                  </option>
                ))}
              </select>

              {/* Participant Filter */}
              <select
                value={selectedParticipant}
                onChange={(e) => setSelectedParticipant(parseInt(e.target.value) || 'all')}
                className="px-3 py-1.5 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all min-w-[180px]"
              >
                <option value="all">All Participants</option>
                {uniqueParticipants.map((participant) => (
                  <option key={participant.id} value={participant.id}>
                    {participant.name}
                  </option>
                ))}
              </select>

              {/* Date From */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-slate-600">From:</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                />
              </div>

              {/* Date To */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-slate-600">To:</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                />
              </div>

              {/* Sort By */}
              <div className="flex items-center gap-2 ml-auto">
                <label className="text-sm text-slate-600">Sort:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                >
                  <option value="date">Date</option>
                  <option value="topic">Topic</option>
                  <option value="project">Project</option>
                </select>

                {/* Sort Order Toggle */}
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="p-1.5 border border-steel-300 rounded-xl hover:bg-steel-50 transition-all"
                  title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                >
                  {sortOrder === 'asc' ? (
                    <ArrowUp className="w-4 h-4 text-slate-600" />
                  ) : (
                    <ArrowDown className="w-4 h-4 text-slate-600" />
                  )}
                </button>
              </div>

              {/* Clear Filters Button */}
              {(selectedProject !== 'all' || selectedTopic !== 'all' || selectedStatus !== 'all' || selectedSupervisor !== 'all' || selectedParticipant !== 'all' || dateFrom || dateTo) && (
                <button
                  onClick={() => {
                    setSelectedProject('all');
                    setSelectedTopic('all');
                    setSelectedStatus('all');
                    setSelectedSupervisor('all');
                    setSelectedParticipant('all');
                    setDateFrom('');
                    setDateTo('');
                  }}
                  className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all flex items-center gap-1"
                >
                  <X className="w-3.5 h-3.5" />
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Results Summary */}
        {(selectedProject !== 'all' || selectedTopic !== 'all' || selectedStatus !== 'all' || selectedSupervisor !== 'all' || selectedParticipant !== 'all' || dateFrom || dateTo || searchTerm || activeQuickFilter) && (
          <div className="bg-brand-50 border border-brand-200 rounded-xl px-4 py-2 mb-4">
            <p className="text-sm text-brand-800">
              <span className="font-semibold">{filteredTalks.length}</span> of <span className="font-semibold">{toolboxTalks.length}</span> talks match your filters
            </p>
          </div>
        )}

        {/* Table with Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-steel-200 overflow-hidden">
          {/* Project Tabs */}
          <div className="border-b-2 border-brand-500 bg-gradient-to-r from-brand-50 to-steel-50">
            <div className="flex overflow-x-auto gap-1 p-2">
              <button
                onClick={() => setSelectedProject('all')}
                className={`px-4 py-2 text-sm font-bold whitespace-nowrap rounded-xl transition-all ${
                  selectedProject === 'all'
                    ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20'
                    : 'text-slate-600 hover:text-brand-600 hover:bg-white'
                }`}
              >
                All Projects
                <span className={`ml-2 text-xs ${selectedProject === 'all' ? 'text-brand-100' : 'text-slate-400'}`}>
                  ({toolboxTalks.length})
                </span>
              </button>
              {projects.map((project) => {
                const projectCount = toolboxTalks.filter(t => t.project === project.name).length;
                return (
                  <button
                    key={project.id}
                    onClick={() => setSelectedProject(project.name)}
                    className={`px-4 py-2 text-sm font-bold whitespace-nowrap rounded-xl transition-all ${
                      selectedProject === project.name
                        ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20'
                        : 'text-slate-600 hover:text-brand-600 hover:bg-white'
                    }`}
                  >
                    {project.name}
                    <span className={`ml-2 text-xs ${selectedProject === project.name ? 'text-brand-100' : 'text-slate-400'}`}>
                      ({projectCount})
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Table Content */}
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
              <p className="mt-2 text-slate-600">Loading...</p>
            </div>
          ) : filteredTalks.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-600">No toolbox talks found</p>
              <button
                onClick={handleCreate}
                className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-brand-500 hover:bg-brand-600 text-white text-sm rounded-xl transition-all duration-200 font-medium shadow-sm hover:shadow-md"
              >
                <Plus className="w-3.5 h-3.5" />
                Create your first toolbox talk
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gradient-to-r from-brand-50 to-steel-50 border-b-2 border-brand-500">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-black text-slate-700 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-4 text-left text-xs font-black text-slate-700 uppercase tracking-wider">Project</th>
                  <th className="px-6 py-4 text-left text-xs font-black text-slate-700 uppercase tracking-wider">Topic</th>
                  <th className="px-6 py-4 text-left text-xs font-black text-slate-700 uppercase tracking-wider">Supervisor/Lead</th>
                  <th className="px-6 py-4 text-left text-xs font-black text-slate-700 uppercase tracking-wider">Created By</th>
                  <th className="px-6 py-4 text-left text-xs font-black text-slate-700 uppercase tracking-wider">Participants</th>
                  <th className="px-6 py-4 text-left text-xs font-black text-slate-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-right text-xs font-black text-slate-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {filteredTalks.map((talk) => {
                  const createdByUser = people.find(p => p.id === talk.created_by);
                  const participantCount = talk.participants ? talk.participants.split(',').filter(id => id.trim()).length : 0;
                  const signedCount = Object.keys(talk.participant_signatures || {}).length;

                  return (
                    <tr key={talk.id} className="hover:bg-brand-50/30 transition-colors border-b border-steel-100 last:border-b-0">
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-brand-100 rounded-xl flex flex-col items-center justify-center">
                            <span className="text-xs font-bold text-brand-700 leading-none">{new Date(talk.date).getDate()}</span>
                            <span className="text-[9px] font-medium text-brand-500 uppercase">{new Date(talk.date).toLocaleDateString('en', { month: 'short' })}</span>
                          </div>
                          <span className="text-slate-600 text-xs">{new Date(talk.date).getFullYear()}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">{talk.project}</td>
                      <td className="px-6 py-4 text-sm text-slate-700">{talk.topic}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{talk.conductor}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {createdByUser ? `${createdByUser.first_name} ${createdByUser.last_name}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-steel-100 rounded-lg w-fit">
                          <Users className="w-3.5 h-3.5 text-slate-500" />
                          <span className="font-semibold text-slate-700">{participantCount}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {talk.is_shared ? (
                          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-100 text-green-700 rounded-lg w-fit">
                            <CheckCircle className="w-3.5 h-3.5" />
                            <span className="font-medium">Shared ({signedCount}/{participantCount})</span>
                          </div>
                        ) : (
                          <span className="px-2.5 py-1 bg-steel-100 text-slate-500 rounded-lg text-xs font-medium">Not shared</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-1 bg-steel-100 rounded-xl p-1">
                          {!talk.is_shared && (
                            <button
                              onClick={() => handleShare(talk)}
                              className="p-2 text-green-600 hover:text-white hover:bg-green-500 rounded-lg transition-all"
                              title="Share with participants"
                            >
                              <Share2 className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handlePreviewPDF(talk)}
                            className="p-2 text-brand-600 hover:text-white hover:bg-brand-500 rounded-lg transition-all"
                            title="Preview PDF"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDownloadPDF(talk)}
                            className="p-2 text-brand-600 hover:text-white hover:bg-brand-500 rounded-lg transition-all"
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleView(talk)}
                            className="p-2 text-slate-600 hover:text-white hover:bg-slate-600 rounded-lg transition-all"
                            title="View details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleCopy(talk)}
                            className="p-2 text-slate-600 hover:text-white hover:bg-slate-600 rounded-lg transition-all"
                            title="Copy to new talk"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(talk)}
                            className="p-2 text-slate-600 hover:text-white hover:bg-slate-600 rounded-lg transition-all"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(talk.id)}
                            className="p-2 text-red-600 hover:text-white hover:bg-red-500 rounded-lg transition-all"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal - Slide from Right */}
      {showModal && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 animate-fadeIn"
            onClick={() => setShowModal(false)}
          />
          {/* Slide-in Panel */}
          <div className="fixed inset-y-0 right-0 w-full max-w-2xl bg-white shadow-2xl z-50 animate-slideInRight overflow-hidden">
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="flex-shrink-0 border-b-2 border-brand-500 bg-gradient-to-r from-brand-50 to-steel-50 px-6 py-5 flex items-center justify-between">
                <h2 className="text-xl font-black text-slate-900 uppercase tracking-wide">
                  {editingTalk ? 'Edit Toolbox Talk' : 'New Toolbox Talk'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 text-slate-400 hover:text-white hover:bg-brand-500 rounded-xl transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto">

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Mode Toggle - only show for new toolbox talks */}
              {!editingTalk && (
                <div className="bg-steel-50 rounded-xl p-4 border border-steel-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-slate-900">Creation Mode</h3>
                      <p className="text-sm text-slate-500">
                        {isPdfOnlyMode
                          ? 'Upload a ready-made PDF document with minimal details'
                          : 'Fill in all details manually'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setIsPdfOnlyMode(false)}
                        className={`px-3 py-1.5 text-sm rounded-xl transition-all ${
                          !isPdfOnlyMode
                            ? 'bg-brand-500 text-white'
                            : 'bg-white text-slate-700 border border-steel-300 hover:bg-steel-50'
                        }`}
                      >
                        Full Form
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsPdfOnlyMode(true)}
                        className={`px-3 py-1.5 text-sm rounded-xl transition-all ${
                          isPdfOnlyMode
                            ? 'bg-brand-500 text-white'
                            : 'bg-white text-slate-700 border border-steel-300 hover:bg-steel-50'
                        }`}
                      >
                        <span className="flex items-center gap-1">
                          <Upload className="w-4 h-4" />
                          PDF Upload
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* PDF-Only Mode - Simplified Form */}
              {isPdfOnlyMode && !editingTalk ? (
                <div className="space-y-6">
                  {/* PDF Upload - Primary */}
                  <div className="border-2 border-dashed border-steel-300 rounded-xl p-6 hover:border-brand-500 transition-all">
                    <label className="cursor-pointer block">
                      <div className="flex flex-col items-center justify-center gap-3">
                        <div className={`p-4 rounded-full ${formData.pdf_document ? 'bg-green-100' : 'bg-steel-100'}`}>
                          {formData.pdf_document ? (
                            <CheckCircle className="w-8 h-8 text-green-600" />
                          ) : (
                            <Upload className="w-8 h-8 text-slate-400" />
                          )}
                        </div>
                        <div className="text-center">
                          <p className="font-medium text-slate-900">
                            {formData.pdf_document ? formData.pdf_document.name : 'Click to upload PDF document'}
                          </p>
                          <p className="text-sm text-slate-500 mt-1">
                            {formData.pdf_document ? 'Click to change file' : 'Upload your ready-made toolbox talk document'}
                          </p>
                        </div>
                      </div>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setFormData({ ...formData, pdf_document: file });
                            // Auto-fill topic from filename if empty
                            if (!formData.topic) {
                              const topicFromFile = file.name.replace(/\.pdf$/i, '').replace(/[-_]/g, ' ');
                              setFormData(prev => ({ ...prev, pdf_document: file, topic: topicFromFile }));
                            }
                          }
                        }}
                        className="hidden"
                      />
                    </label>
                    {formData.pdf_document && (
                      <div className="mt-4 flex justify-center">
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, pdf_document: null })}
                          className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                        >
                          <X className="w-4 h-4" />
                          Remove file
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Minimal Required Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Date *</label>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Project *</label>
                      <select
                        value={formData.project}
                        onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                        className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                        required
                      >
                        <option value="">Select a project...</option>
                        {projects.map((project) => (
                          <option key={project.id} value={project.name}>
                            {project.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Topic / Title *</label>
                    <input
                      type="text"
                      value={formData.topic}
                      onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                      className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                      placeholder="e.g., Working at Heights Safety"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Conductor *</label>
                    <input
                      type="text"
                      value={formData.conductor}
                      onChange={(e) => setFormData({ ...formData, conductor: e.target.value })}
                      className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                      required
                    />
                  </div>

                  {/* Optional: Participants */}
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">
                      Participants ({selectedParticipants.length} selected) - Optional
                    </label>

                    {/* Show selected participants first */}
                    {selectedParticipants.length > 0 && (
                      <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded-xl">
                        <p className="text-xs text-green-700 mb-2 font-medium">Selected:</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedParticipants.map(personId => {
                            const person = people.find(p => p.id === personId);
                            return person ? (
                              <span
                                key={personId}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                              >
                                {person.first_name} {person.last_name}
                                <button
                                  type="button"
                                  onClick={() => handleToggleParticipant(personId)}
                                  className="hover:text-green-900"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}

                    <div className="relative mb-2">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search people to add..."
                        value={participantSearchTerm}
                        onChange={(e) => setParticipantSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                      />
                    </div>
                    <div className="border border-steel-300 rounded-xl max-h-40 overflow-y-auto">
                      {filteredPeople.slice(0, 15).map(person => (
                        <label
                          key={person.id}
                          className={`flex items-center gap-3 p-2 hover:bg-steel-50 cursor-pointer border-b border-steel-100 last:border-b-0 ${
                            selectedParticipants.includes(person.id) ? 'bg-green-50' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedParticipants.includes(person.id)}
                            onChange={() => handleToggleParticipant(person.id)}
                            className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                          />
                          <span className="text-sm text-slate-700">
                            {person.first_name} {person.last_name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                /* Full Form Mode */
                <>
              {/* Basic Info */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Time</label>
                  <input
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Project</label>
                  <select
                    value={formData.project}
                    onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                    className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                    required
                  >
                    <option value="">Select a project...</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.name}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Topic</label>
                <input
                  type="text"
                  value={formData.topic}
                  onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                  className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                  placeholder="e.g., Working at Heights Safety"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Supervisor/Lead Name</label>
                  <input
                    type="text"
                    value={formData.conductor}
                    onChange={(e) => setFormData({ ...formData, conductor: e.target.value })}
                    className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Signature</label>
                  <input
                    type="text"
                    value={formData.signature}
                    onChange={(e) => setFormData({ ...formData, signature: e.target.value })}
                    className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                    placeholder="Supervisor signature..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">
                  Participants ({selectedParticipants.length} selected)
                </label>

                {/* Search Input */}
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search people..."
                    value={participantSearchTerm}
                    onChange={(e) => setParticipantSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                  />
                </div>

                {/* Selected Participants */}
                {selectedParticipants.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-2">
                    {selectedParticipants.map(personId => {
                      const person = people.find(p => p.id === personId);
                      if (!person) return null;
                      return (
                        <span key={personId} className="inline-flex items-center gap-1 px-3 py-1 bg-brand-100 text-brand-800 rounded-full text-sm">
                          {person.first_name} {person.last_name}
                          <button
                            type="button"
                            onClick={() => handleToggleParticipant(personId)}
                            className="hover:text-brand-900"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* People List */}
                <div className="border border-steel-300 rounded-xl max-h-48 overflow-y-auto">
                  {filteredPeople.length === 0 ? (
                    <div className="p-3 text-center text-slate-500 text-sm">No people found</div>
                  ) : (
                    filteredPeople.map(person => (
                      <label
                        key={person.id}
                        className="flex items-center gap-3 p-3 hover:bg-steel-50 cursor-pointer border-b border-steel-100 last:border-b-0 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedParticipants.includes(person.id)}
                          onChange={() => handleToggleParticipant(person.id)}
                          className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-slate-900">
                            {person.first_name} {person.last_name}
                          </div>
                          {person.email && (
                            <div className="text-xs text-slate-500">{person.email}</div>
                          )}
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Work Activities / Tasks</label>

                {/* Add Work Activity Input */}
                <div className="relative" ref={activityInputRef}>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newWorkActivity}
                      onChange={(e) => {
                        setNewWorkActivity(e.target.value);
                        setShowActivitySuggestions(true);
                      }}
                      onFocus={() => setShowActivitySuggestions(true)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddWorkActivity())}
                      placeholder="Type or select from previous activities..."
                      className="flex-1 px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => handleAddWorkActivity()}
                      className="px-3 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-xl transition-all shadow-sm hover:shadow-md"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Activity Suggestions Dropdown */}
                  {showActivitySuggestions && getFilteredSuggestions().length > 0 && (
                    <div className="absolute z-10 w-full bg-white border border-steel-300 rounded-xl shadow-lg max-h-48 overflow-y-auto mb-2">
                      {getFilteredSuggestions().map((activity, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleAddWorkActivity(activity)}
                          className="w-full text-left px-3 py-2 hover:bg-steel-100 text-sm text-slate-700 border-b border-steel-100 last:border-b-0 transition-colors"
                        >
                          {activity}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Work Activities List */}
                {formData.work_activities.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {formData.work_activities.map((activity, index) => {
                      // Support both old string format and new object format
                      const activityName = typeof activity === 'string' ? activity : activity.name;
                      const activityTurbine = typeof activity === 'string' ? '' : (activity.turbine || '');

                      return (
                        <div key={index} className="bg-steel-50 p-3 rounded-xl hover:bg-steel-100 transition-colors group">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-2.5">
                              <div className="w-2 h-2 bg-brand-500 rounded-full"></div>
                            </div>
                            <div className="flex-1">
                              <input
                                type="text"
                                value={activityName}
                                onChange={(e) => handleEditWorkActivity(index, 'name', e.target.value)}
                                className="w-full px-2 py-1.5 text-sm text-slate-700 bg-white border border-steel-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                                placeholder="Task name..."
                              />
                              <div className="flex items-center gap-2 mt-2">
                                <span className="text-xs text-slate-500">Turbine:</span>
                                <input
                                  type="text"
                                  value={activityTurbine}
                                  onChange={(e) => handleEditWorkActivity(index, 'turbine', e.target.value)}
                                  className="flex-1 px-2 py-1 text-xs text-slate-600 bg-white border border-steel-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                                  placeholder="e.g., T01, T02..."
                                />
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveWorkActivity(index)}
                              className="opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-700 hover:bg-red-50 p-1.5 rounded-xl transition-all"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {formData.work_activities.length === 0 && (
                  <div className="text-sm text-slate-500 italic py-2">No activities added yet</div>
                )}
              </div>

              {/* Custom Fields */}
              <div>
                <h4 className="text-base font-semibold text-slate-900 mb-2">Custom Fields</h4>
                <p className="text-sm text-slate-600 mb-4">Add your own custom fields to personalize this form</p>

                {/* Add Custom Field */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <input
                    type="text"
                    value={newCustomField.label}
                    onChange={(e) => setNewCustomField({ ...newCustomField, label: e.target.value })}
                    placeholder="Field name (e.g., Location, Equipment ID)..."
                    className="px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm transition-all"
                  />
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newCustomField.value}
                      onChange={(e) => setNewCustomField({ ...newCustomField, value: e.target.value })}
                      placeholder="Value..."
                      className="flex-1 px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm transition-all"
                    />
                    <button
                      type="button"
                      onClick={handleAddCustomField}
                      className="px-3 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-xl transition-all shadow-sm hover:shadow-md"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Custom Fields List */}
                {formData.custom_fields.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {formData.custom_fields.map((field, index) => (
                      <div key={index} className="grid grid-cols-2 gap-2 bg-steel-50 p-3 rounded-xl group">
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">{field.label}</label>
                          <input
                            type="text"
                            value={field.value}
                            onChange={(e) => handleUpdateCustomField(index, 'value', e.target.value)}
                            className="w-full px-2 py-1.5 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => handleRemoveCustomField(index)}
                            className="opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-700 hover:bg-red-50 p-2 rounded-xl transition-all ml-auto"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {formData.custom_fields.length === 0 && (
                  <div className="text-sm text-slate-500 italic py-2 mb-4">No custom fields added yet</div>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Additional Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                  rows="3"
                  placeholder="Any additional information..."
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">HSE Comments</label>
                <textarea
                  value={formData.hse_comments}
                  onChange={(e) => setFormData({ ...formData, hse_comments: e.target.value })}
                  className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                  rows="3"
                  placeholder="Health, Safety & Environment comments..."
                />
              </div>

              {/* PDF Document Upload */}
              <div className="border-t border-steel-200 pt-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Upload Ready-Made Toolbox Talk (Optional)</h3>
                <p className="text-sm text-slate-500 mb-3">
                  You can upload an existing PDF document for this toolbox talk. This is useful when you have a pre-made toolbox talk document.
                </p>
                <div className="flex items-center gap-4">
                  <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-steel-300 rounded-xl cursor-pointer hover:border-brand-500 hover:bg-steel-50 transition-all">
                    <div className="flex items-center gap-2">
                      <Upload className="w-5 h-5 text-slate-400" />
                      <span className="text-sm text-slate-600">
                        {formData.pdf_document?.name || 'Click to upload PDF document'}
                      </span>
                    </div>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setFormData({ ...formData, pdf_document: file });
                        }
                      }}
                      className="hidden"
                    />
                  </label>
                  {formData.pdf_document && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, pdf_document: null })}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-all"
                      title="Remove PDF"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
                {/* Show existing PDF link if editing */}
                {editingTalk?.pdf_document && !formData.pdf_document && (
                  <div className="mt-2 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-brand-600" />
                    <a
                      href={editingTalk.pdf_document}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-brand-600 hover:text-brand-800 underline"
                    >
                      View current PDF document
                    </a>
                  </div>
                )}
              </div>

              {/* Subjects Discussed */}
              <div className="border-t border-steel-200 pt-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Subjects Discussed (tick where applicable)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="flex items-center gap-3 cursor-pointer hover:bg-steel-50 p-2 rounded-xl transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.type_of_operation || false}
                      onChange={(e) => setFormData({ ...formData, type_of_operation: e.target.checked })}
                      className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700">Type of Operation</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer hover:bg-steel-50 p-2 rounded-xl transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.crane_requirements || false}
                      onChange={(e) => setFormData({ ...formData, crane_requirements: e.target.checked })}
                      className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700">Crane Requirements</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer hover:bg-steel-50 p-2 rounded-xl transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.methods_procedures || false}
                      onChange={(e) => setFormData({ ...formData, methods_procedures: e.target.checked })}
                      className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700">Methods & Procedures</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer hover:bg-steel-50 p-2 rounded-xl transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.potential_hazards || false}
                      onChange={(e) => setFormData({ ...formData, potential_hazards: e.target.checked })}
                      className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700">Potential Hazards</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer hover:bg-steel-50 p-2 rounded-xl transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.permit_to_work || false}
                      onChange={(e) => setFormData({ ...formData, permit_to_work: e.target.checked })}
                      className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700">Permit to Work</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer hover:bg-steel-50 p-2 rounded-xl transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.equipment_to_be_used || false}
                      onChange={(e) => setFormData({ ...formData, equipment_to_be_used: e.target.checked })}
                      className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700">Equipment to be Used</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer hover:bg-steel-50 p-2 rounded-xl transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.safety_spill_equipment || false}
                      onChange={(e) => setFormData({ ...formData, safety_spill_equipment: e.target.checked })}
                      className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700">Safety / Spill Equipment</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer hover:bg-steel-50 p-2 rounded-xl transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.materials_chemicals || false}
                      onChange={(e) => setFormData({ ...formData, materials_chemicals: e.target.checked })}
                      className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700">Materials / Chemicals</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer hover:bg-steel-50 p-2 rounded-xl transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.responsibilities || false}
                      onChange={(e) => setFormData({ ...formData, responsibilities: e.target.checked })}
                      className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700">Responsibilities</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer hover:bg-steel-50 p-2 rounded-xl transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.isolations_necessary || false}
                      onChange={(e) => setFormData({ ...formData, isolations_necessary: e.target.checked })}
                      className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700">Isolations Necessary</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer hover:bg-steel-50 p-2 rounded-xl transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.access_egress || false}
                      onChange={(e) => setFormData({ ...formData, access_egress: e.target.checked })}
                      className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700">Access / Egress</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer hover:bg-steel-50 p-2 rounded-xl transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.impact_adjacent_work || false}
                      onChange={(e) => setFormData({ ...formData, impact_adjacent_work: e.target.checked })}
                      className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700">Impact on Adjacent Work</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer hover:bg-steel-50 p-2 rounded-xl transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.tests_monitoring || false}
                      onChange={(e) => setFormData({ ...formData, tests_monitoring: e.target.checked })}
                      className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700">Tests / Monitoring</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer hover:bg-steel-50 p-2 rounded-xl transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.environmental_hazards || false}
                      onChange={(e) => setFormData({ ...formData, environmental_hazards: e.target.checked })}
                      className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700">Environmental Hazards</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer hover:bg-steel-50 p-2 rounded-xl transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.work_environment || false}
                      onChange={(e) => setFormData({ ...formData, work_environment: e.target.checked })}
                      className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700">Work Environment</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer hover:bg-steel-50 p-2 rounded-xl transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.waste_management || false}
                      onChange={(e) => setFormData({ ...formData, waste_management: e.target.checked })}
                      className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700">Waste Management</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer hover:bg-steel-50 p-2 rounded-xl transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.general_other_info || false}
                      onChange={(e) => setFormData({ ...formData, general_other_info: e.target.checked })}
                      className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700">General / Other Information</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer hover:bg-steel-50 p-2 rounded-xl transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.flora_fauna_archeological || false}
                      onChange={(e) => setFormData({ ...formData, flora_fauna_archeological: e.target.checked })}
                      className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700">Flora / Fauna / Archeological</span>
                  </label>
                </div>

                {/* Custom Subjects */}
                <div className="mt-6 border-t border-steel-200 pt-6">
                  <h4 className="text-base font-semibold text-slate-900 mb-3">Custom Subjects</h4>

                  {/* Add Custom Subject */}
                  <div className="flex gap-2 mb-4">
                    <input
                      type="text"
                      value={newCustomSubject}
                      onChange={(e) => setNewCustomSubject(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCustomSubject())}
                      placeholder="Add your own subject..."
                      className="flex-1 px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm transition-all"
                    />
                    <button
                      type="button"
                      onClick={handleAddCustomSubject}
                      className="px-3 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-xl transition-all shadow-sm hover:shadow-md"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Custom Subjects List */}
                  {formData.custom_subjects.length > 0 && (
                    <div className="space-y-2">
                      {formData.custom_subjects.map((subject, index) => (
                        <div key={index} className="flex items-center gap-3 bg-steel-50 p-2 rounded-xl hover:bg-steel-100 transition-colors">
                          <label className="flex items-center gap-3 cursor-pointer flex-1">
                            <input
                              type="checkbox"
                              checked={subject.checked}
                              onChange={() => handleToggleCustomSubject(index)}
                              className="w-4 h-4 text-black border-steel-300 rounded focus:ring-brand-500"
                            />
                            <span className="text-sm text-slate-700">{subject.text}</span>
                          </label>
                          <button
                            type="button"
                            onClick={() => handleRemoveCustomSubject(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1.5 rounded-xl transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              </>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-steel-200">
                {/* Save as Template - hide in PDF-only mode */}
                {!isPdfOnlyMode ? (
                  <button
                    type="button"
                    onClick={() => setShowSaveTemplateModal(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-xl transition-all font-medium shadow-sm hover:shadow-md"
                  >
                    <Save className="w-4 h-4" />
                    Save as Template
                  </button>
                ) : (
                  <div /> /* Spacer */
                )}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-steel-300 text-slate-700 rounded-xl hover:bg-steel-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-xl transition-all font-medium shadow-sm hover:shadow-md"
                  >
                    {editingTalk ? 'Update' : 'Create'} Toolbox Talk
                  </button>
                </div>
              </div>
            </form>
              </div>
            </div>
          </div>
        </>
      )}

      {/* View Modal (Read-only) - Slide from Right */}
      {showViewModal && viewingTalk && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 animate-fadeIn"
            onClick={() => setShowViewModal(false)}
          />
          {/* Slide-in Panel */}
          <div className="fixed inset-y-0 right-0 w-full max-w-2xl bg-white shadow-2xl z-50 animate-slideInRight overflow-hidden">
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="flex-shrink-0 border-b-2 border-brand-500 bg-gradient-to-r from-brand-50 to-steel-50 px-6 py-5">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xl font-black text-slate-900 uppercase tracking-wide">Toolbox Talk Details</h2>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="p-2 text-slate-400 hover:text-white hover:bg-brand-500 rounded-xl transition-all"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePreviewPDF(viewingTalk)}
                    className="px-3 py-1.5 bg-brand-500 hover:bg-brand-600 text-white rounded-xl transition-all text-sm flex items-center gap-2 shadow-lg shadow-brand-500/20"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    Preview
                  </button>
                  <button
                    onClick={() => handleDownloadPDF(viewingTalk)}
                    className="px-3 py-1.5 bg-brand-500 hover:bg-brand-600 text-white rounded-xl transition-all text-sm flex items-center gap-2 shadow-lg shadow-brand-500/20"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download
                  </button>
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      handleCopy(viewingTalk);
                    }}
                    className="px-3 py-1.5 bg-white hover:bg-brand-50 text-brand-600 border border-brand-300 rounded-xl transition-all text-sm flex items-center gap-2"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    Copy
                  </button>
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      handleEdit(viewingTalk);
                    }}
                    className="px-3 py-1.5 bg-white hover:bg-brand-50 text-brand-600 border border-brand-300 rounded-xl transition-all text-sm flex items-center gap-2"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                    Edit
                  </button>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-steel-50 rounded-xl p-4">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Date</label>
                  <p className="text-slate-900 font-semibold">{new Date(viewingTalk.date).toLocaleDateString()}</p>
                </div>
                <div className="bg-steel-50 rounded-xl p-4">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Time</label>
                  <p className="text-slate-900 font-semibold">{viewingTalk.time || '-'}</p>
                </div>
                <div className="bg-steel-50 rounded-xl p-4">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Project</label>
                  <p className="text-slate-900 font-semibold">{viewingTalk.project}</p>
                </div>
              </div>

              <div className="bg-brand-50 rounded-xl p-4 border border-brand-200">
                <label className="block text-xs font-bold text-brand-600 uppercase tracking-wide mb-1">Topic</label>
                <p className="text-slate-900 font-semibold text-lg">{viewingTalk.topic}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-steel-50 rounded-xl p-4">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Supervisor/Lead Name</label>
                  <p className="text-slate-900 font-semibold">{viewingTalk.conductor}</p>
                </div>
                <div className="bg-steel-50 rounded-xl p-4">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Signature</label>
                  <p className="text-slate-900 font-semibold">{viewingTalk.signature || '-'}</p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Participants</label>
                <div className="text-slate-900">
                  {viewingTalk.participants ? (
                    <div className="flex flex-wrap gap-2">
                      {viewingTalk.participants.split(',').map((id, index) => {
                        const personId = parseInt(id.trim());
                        const person = people.find(p => p.id === personId);
                        if (!person) return null;
                        return (
                          <span key={index} className="inline-flex items-center px-3 py-1.5 bg-brand-100 text-brand-800 rounded-xl text-sm font-medium">
                            {person.first_name} {person.last_name}
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-slate-500">-</p>
                  )}
                </div>
              </div>

              {viewingTalk.work_activities && viewingTalk.work_activities.length > 0 && (
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Work Activities / Tasks</label>
                  <div className="space-y-2">
                    {viewingTalk.work_activities.map((activity, index) => {
                      // Support both old string format and new object format
                      const activityName = typeof activity === 'string' ? activity : activity.name;
                      const activityTurbine = typeof activity === 'string' ? '' : (activity.turbine || '');

                      return (
                        <div key={index} className="flex items-start gap-3 bg-steel-50 p-3 rounded-xl">
                          <div className="flex-shrink-0 mt-0.5">
                            <div className="w-2 h-2 bg-brand-500 rounded-full"></div>
                          </div>
                          <div className="flex-1">
                            <div className="text-sm text-slate-700">{activityName}</div>
                            {activityTurbine && (
                              <div className="text-xs text-slate-500 mt-1">
                                <span className="font-medium">Turbine:</span> {activityTurbine}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {viewingTalk.notes && (
                <div className="bg-steel-50 rounded-xl p-4">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Additional Notes</label>
                  <p className="text-slate-700 whitespace-pre-wrap">{viewingTalk.notes}</p>
                </div>
              )}

              {viewingTalk.hse_comments && (
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                  <label className="block text-xs font-bold text-amber-600 uppercase tracking-wide mb-2">HSE Comments</label>
                  <p className="text-slate-700 whitespace-pre-wrap">{viewingTalk.hse_comments}</p>
                </div>
              )}

              {/* PDF Document */}
              {viewingTalk.pdf_document && (
                <div className="border-t-2 border-brand-500 pt-6">
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide mb-4">Attached Document</h3>
                  <a
                    href={viewingTalk.pdf_document}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition-all shadow-lg shadow-brand-500/20"
                  >
                    <FileText className="w-5 h-5" />
                    <span className="font-medium">View PDF Document</span>
                    <Download className="w-4 h-4" />
                  </a>
                </div>
              )}

              {/* Subjects Discussed */}
              <div className="border-t-2 border-steel-200 pt-6">
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide mb-4">Subjects Discussed</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {viewingTalk.type_of_operation && <div className="flex items-center gap-2"><span className="text-green-600">✓</span><span className="text-sm">Type of Operation</span></div>}
                  {viewingTalk.crane_requirements && <div className="flex items-center gap-2"><span className="text-green-600">✓</span><span className="text-sm">Crane Requirements</span></div>}
                  {viewingTalk.methods_procedures && <div className="flex items-center gap-2"><span className="text-green-600">✓</span><span className="text-sm">Methods & Procedures</span></div>}
                  {viewingTalk.potential_hazards && <div className="flex items-center gap-2"><span className="text-green-600">✓</span><span className="text-sm">Potential Hazards</span></div>}
                  {viewingTalk.permit_to_work && <div className="flex items-center gap-2"><span className="text-green-600">✓</span><span className="text-sm">Permit to Work</span></div>}
                  {viewingTalk.equipment_to_be_used && <div className="flex items-center gap-2"><span className="text-green-600">✓</span><span className="text-sm">Equipment to be Used</span></div>}
                  {viewingTalk.safety_spill_equipment && <div className="flex items-center gap-2"><span className="text-green-600">✓</span><span className="text-sm">Safety / Spill Equipment</span></div>}
                  {viewingTalk.materials_chemicals && <div className="flex items-center gap-2"><span className="text-green-600">✓</span><span className="text-sm">Materials / Chemicals</span></div>}
                  {viewingTalk.responsibilities && <div className="flex items-center gap-2"><span className="text-green-600">✓</span><span className="text-sm">Responsibilities</span></div>}
                  {viewingTalk.isolations_necessary && <div className="flex items-center gap-2"><span className="text-green-600">✓</span><span className="text-sm">Isolations Necessary</span></div>}
                  {viewingTalk.access_egress && <div className="flex items-center gap-2"><span className="text-green-600">✓</span><span className="text-sm">Access / Egress</span></div>}
                  {viewingTalk.impact_adjacent_work && <div className="flex items-center gap-2"><span className="text-green-600">✓</span><span className="text-sm">Impact on Adjacent Work</span></div>}
                  {viewingTalk.tests_monitoring && <div className="flex items-center gap-2"><span className="text-green-600">✓</span><span className="text-sm">Tests / Monitoring</span></div>}
                  {viewingTalk.environmental_hazards && <div className="flex items-center gap-2"><span className="text-green-600">✓</span><span className="text-sm">Environmental Hazards</span></div>}
                  {viewingTalk.work_environment && <div className="flex items-center gap-2"><span className="text-green-600">✓</span><span className="text-sm">Work Environment</span></div>}
                  {viewingTalk.waste_management && <div className="flex items-center gap-2"><span className="text-green-600">✓</span><span className="text-sm">Waste Management</span></div>}
                  {viewingTalk.general_other_info && <div className="flex items-center gap-2"><span className="text-green-600">✓</span><span className="text-sm">General / Other Information</span></div>}
                  {viewingTalk.flora_fauna_archeological && <div className="flex items-center gap-2"><span className="text-green-600">✓</span><span className="text-sm">Flora / Fauna / Archeological</span></div>}
                </div>

                {/* Custom Subjects */}
                {viewingTalk.custom_subjects && viewingTalk.custom_subjects.length > 0 && (
                  <div className="mt-4 border-t border-steel-200 pt-4">
                    <h4 className="text-base font-semibold text-slate-900 mb-3">Custom Subjects</h4>
                    <div className="space-y-2">
                      {viewingTalk.custom_subjects.map((subject, index) => (
                        subject.checked && (
                          <div key={index} className="flex items-center gap-2">
                            <span className="text-green-600">✓</span>
                            <span className="text-sm">{subject.text}</span>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}

                {/* Custom Fields */}
                {viewingTalk.custom_fields && viewingTalk.custom_fields.length > 0 && (
                  <div className="mt-4 border-t border-steel-200 pt-4">
                    <h4 className="text-base font-semibold text-slate-900 mb-3">Custom Fields</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {viewingTalk.custom_fields.map((field, index) => (
                        <div key={index}>
                          <label className="block text-xs font-medium text-slate-500 mb-1">{field.label}</label>
                          <p className="text-sm text-slate-900">{field.value || '-'}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Signatures - People who signed */}
                {viewingTalk.participant_signatures && Object.keys(viewingTalk.participant_signatures).length > 0 && (
                  <div className="mt-4 border-t border-steel-200 pt-4">
                    <h4 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      Signatures ({Object.keys(viewingTalk.participant_signatures).length})
                    </h4>
                    <div className="border border-steel-200 rounded-xl overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-steel-50 border-b border-steel-200">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Time</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-steel-200">
                          {Object.entries(viewingTalk.participant_signatures).map(([userId, signatureData]) => {
                            const personId = parseInt(userId);
                            const person = people.find(p => p.id === personId);
                            if (!person) return null;

                            const signedAt = signatureData.signed_at;
                            const signedDate = signedAt ? new Date(signedAt) : null;

                            return (
                              <tr key={userId} className="hover:bg-steel-50">
                                <td className="px-4 py-2 text-sm">
                                  <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                                    <span className="font-medium text-slate-900">
                                      {person.first_name} {person.last_name}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-4 py-2 text-sm text-slate-900">
                                  {signedDate ? signedDate.toLocaleDateString() : '-'}
                                </td>
                                <td className="px-4 py-2 text-sm text-slate-600">
                                  {signedDate ? signedDate.toLocaleTimeString() : '-'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Signing Modal - Auto-show for unsigned toolbox talks */}
      {showSigningModal && talksToSign.length > 0 && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 animate-fadeIn"
            onClick={() => setShowSigningModal(false)}
          />
          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden animate-slideInRight">
              {/* Header */}
              <div className="bg-brand-500 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white rounded-full p-2">
                    <FileText className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Toolbox Talks to Sign</h2>
                    <p className="text-brand-100 text-sm">You have {talksToSign.length} toolbox talk(s) awaiting your signature</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSigningModal(false)}
                  className="text-white hover:bg-brand-800 rounded-full p-2 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-100px)]">
                <div className="space-y-4">
                  {talksToSign.map((talk) => (
                    <div key={talk.id} className="border-2 border-brand-200 rounded-xl p-4 hover:border-brand-400 transition-all bg-gradient-to-r from-brand-50 to-white">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg text-slate-900 mb-2">{talk.topic}</h3>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="flex items-center gap-2 text-slate-600">
                              <Calendar className="w-4 h-4" />
                              <span>{new Date(talk.date).toLocaleDateString()}</span>
                            </div>
                            <div className="flex items-center gap-2 text-slate-600">
                              <FileText className="w-4 h-4" />
                              <span>{talk.project}</span>
                            </div>
                            <div className="col-span-2 text-slate-600">
                              <span className="font-medium">Supervisor:</span> {talk.conductor}
                            </div>
                            {talk.work_activities && talk.work_activities.length > 0 && (
                              <div className="col-span-2">
                                <span className="font-medium text-slate-700">Work Activities:</span>
                                <ul className="list-disc list-inside ml-2 mt-1 text-slate-600">
                                  {talk.work_activities.slice(0, 3).map((activity, idx) => {
                                    const activityName = typeof activity === 'string' ? activity : activity.name;
                                    const activityTurbine = typeof activity === 'string' ? '' : (activity.turbine || '');
                                    return (
                                      <li key={idx} className="text-sm">
                                        {activityName}
                                        {activityTurbine && <span className="text-slate-400 ml-1">({activityTurbine})</span>}
                                      </li>
                                    );
                                  })}
                                  {talk.work_activities.length > 3 && (
                                    <li className="text-sm text-slate-500">+{talk.work_activities.length - 3} more...</li>
                                  )}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => handleSignTalk(talk.id)}
                            className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-xl transition-all flex items-center gap-2 shadow-sm hover:shadow-md font-medium"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Sign Now
                          </button>
                          <button
                            onClick={() => handlePreviewPDF(talk)}
                            className="px-4 py-2 bg-steel-100 hover:bg-steel-200 text-slate-700 rounded-xl transition-all flex items-center gap-2 text-sm"
                          >
                            <Eye className="w-4 h-4" />
                            Preview
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {!user.user_signature && (
                  <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                    <div className="flex items-start gap-3">
                      <div className="text-yellow-600 mt-0.5">⚠️</div>
                      <div>
                        <p className="font-medium text-yellow-900">No signature defined</p>
                        <p className="text-sm text-yellow-700 mt-1">
                          You don't have a signature set up in your profile. Please go to your user settings to add your signature before signing toolbox talks.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Template Library Modal */}
      {showTemplateLibrary && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 animate-fadeIn"
            onClick={() => setShowTemplateLibrary(false)}
          />
          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden animate-slideInRight">
              {/* Header */}
              <div className="bg-brand-500 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white rounded-full p-2">
                    <BookTemplate className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Toolbox Talk Templates</h2>
                    <p className="text-brand-100 text-sm">Select a template to quickly create a new toolbox talk</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowTemplateLibrary(false)}
                  className="text-white hover:bg-brand-800 rounded-full p-2 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-100px)]">
                {templates.length === 0 ? (
                  <div className="text-center py-12">
                    <BookTemplate className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 mb-2">No templates yet</p>
                    <p className="text-sm text-slate-500">Create a toolbox talk and save it as a template to see it here</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {templates.map((template) => (
                      <div key={template.id} className="border-2 border-brand-200 rounded-xl p-4 hover:border-brand-400 transition-all bg-gradient-to-r from-brand-50 to-white">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg text-slate-900 mb-1">{template.name}</h3>
                            <p className="text-sm text-slate-600">{template.topic}</p>
                          </div>
                          <button
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1.5 rounded-xl transition-all"
                            title="Delete template"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Template Details Preview */}
                        {template.work_activities && template.work_activities.length > 0 && (
                          <div className="mb-3">
                            <span className="text-xs font-medium text-slate-700">Work Activities:</span>
                            <ul className="list-disc list-inside ml-2 mt-1">
                              {template.work_activities.slice(0, 2).map((activity, idx) => {
                                const activityName = typeof activity === 'string' ? activity : activity.name;
                                const activityTurbine = typeof activity === 'string' ? '' : (activity.turbine || '');
                                return (
                                  <li key={idx} className="text-xs text-slate-600">
                                    {activityName}
                                    {activityTurbine && <span className="text-slate-400 ml-1">({activityTurbine})</span>}
                                  </li>
                                );
                              })}
                              {template.work_activities.length > 2 && (
                                <li className="text-xs text-slate-500">+{template.work_activities.length - 2} more...</li>
                              )}
                            </ul>
                          </div>
                        )}

                        {/* Subjects */}
                        <div className="flex flex-wrap gap-1 mb-3">
                          {template.type_of_operation && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-brand-100 text-brand-800">Type of Operation</span>
                          )}
                          {template.crane_requirements && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-brand-100 text-brand-800">Crane Requirements</span>
                          )}
                          {template.potential_hazards && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-brand-100 text-brand-800">Potential Hazards</span>
                          )}
                          {template.permit_to_work && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-brand-100 text-brand-800">Permit to Work</span>
                          )}
                        </div>

                        <button
                          onClick={() => handleCreateFromTemplate(template)}
                          className="w-full px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-xl transition-all flex items-center justify-center gap-2 shadow-sm hover:shadow-md font-medium"
                        >
                          <Plus className="w-4 h-4" />
                          Use This Template
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Save Template Modal */}
      {showSaveTemplateModal && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[60] animate-fadeIn"
            onClick={() => setShowSaveTemplateModal(false)}
          />
          {/* Modal */}
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-slideInRight">
              {/* Header */}
              <div className="bg-brand-500 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white rounded-full p-2">
                    <Save className="w-5 h-5 text-brand-600" />
                  </div>
                  <h2 className="text-lg font-bold text-white">Save as Template</h2>
                </div>
                <button
                  onClick={() => setShowSaveTemplateModal(false)}
                  className="text-white hover:bg-brand-800 rounded-full p-1.5 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-sm text-slate-600 mb-4">
                  Give this template a name so you can easily find and reuse it later.
                </p>
                <div className="mb-4">
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Template Name</label>
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="e.g., Working at Height - Standard"
                    className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-600 focus:border-transparent transition-all"
                    autoFocus
                    onKeyPress={(e) => e.key === 'Enter' && handleSaveAsTemplate()}
                  />
                </div>
                <div className="bg-brand-50 border border-brand-200 rounded-xl p-3 mb-4">
                  <p className="text-xs text-brand-800">
                    <strong>Note:</strong> This will save all form fields including subjects discussed, work activities, and custom fields as a reusable template.
                  </p>
                </div>
                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={() => setShowSaveTemplateModal(false)}
                    className="px-4 py-2 border border-steel-300 text-slate-700 rounded-xl hover:bg-steel-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveAsTemplate}
                    className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-xl transition-all font-medium shadow-sm hover:shadow-md inline-flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Save Template
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ToolboxTalks;
